import React from "react";
import { motion } from "framer-motion";

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay,
      duration: 0.6,
      ease: "easeOut",
    },
  }),
};

const listItemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.2 + i * 0.08,
      duration: 0.4,
      ease: "easeOut",
    },
  }),
};

export default function AboutPage() {
  return (
    <div className="container py-5">
      {/* HERO */}
      <motion.section
        className="text-center mb-5"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      >
        <motion.h1
          className="display-5 fw-bold"
          layoutId="about-title"
          transition={{ type: "spring", stiffness: 80, damping: 20 }}
        >
          Senior Software Engineer, Educator & Digital Health Innovator
        </motion.h1>

        <motion.p
          className="lead mt-3 text-muted"
          style={{ maxWidth: 800, margin: "0 auto" }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.7 }}
        >
          I build scalable systems, teach with purpose, and design technology
          that improves lives — especially for seniors who deserve accessible,
          human-centered digital health tools.
        </motion.p>
      </motion.section>

      {/* MISSION */}
      <motion.section
        className="mb-5"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        custom={0.2}
      >
        <h2 className="h4 mb-3">My Mission</h2>
        <motion.p
          className="text-muted"
          style={{ whiteSpace: "pre-line", maxWidth: 900 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          My mission is simple: to build technology that matters.
          I believe software should be reliable, accessible, and deeply human.
          Whether I’m architecting a national AI Health Navigator for seniors in New Zealand
          or teaching a student their first line of code, my goal is always the same —
          create clarity, reduce complexity, and empower people.
        </motion.p>
      </motion.section>

      {/* WHAT I DO */}
      <motion.section
        className="mb-5"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        custom={0.3}
      >
        <h2 className="h4 mb-4">What I Do</h2>

        <div className="row g-4">
          {[
            {
              title: "Software Engineering",
              text: "I design and build scalable, secure, production-ready systems — from backend APIs to full-stack platforms.",
            },
            {
              title: "Education & Mentorship",
              text: "I teach programming and mathematics with a practical, confidence-building approach.",
            },
            {
              title: "Digital Health Innovation",
              text: "I’m architecting a national AI Health Navigator for seniors in New Zealand — focused on accessibility, safety, and real-world impact.",
            },
          ].map((item, index) => (
            <motion.div
              key={item.title}
              className="col-md-4"
              variants={listItemVariants}
              initial="hidden"
              animate="visible"
              custom={index}
            >
              <motion.div
                className="p-4 border rounded h-100"
                whileHover={{
                  y: -6,
                  boxShadow: "0 18px 40px rgba(15, 23, 42, 0.15)",
                }}
                transition={{ type: "spring", stiffness: 180, damping: 18 }}
              >
                <h3 className="h6 fw-bold">{item.title}</h3>
                <p className="text-muted mt-2">{item.text}</p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* SKILLS & EXPERTISE */}
      <motion.section
        className="mb-5"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        custom={0.4}
      >
        <h2 className="h4 mb-3">Skills & Expertise</h2>

        <div className="row g-4">
          <motion.div
            className="col-md-6"
            variants={containerVariants}
            custom={0.45}
          >
            <h3 className="h6 fw-bold">Technical Skills</h3>
            <ul className="text-muted mt-2">
              {[
                "Node.js, Express.js, React, MongoDB",
                "RESTful API Design, Authentication, RBAC",
                "System Architecture, Security, Scalability",
                "UI/UX for real-world usability",
              ].map((skill, i) => (
                <motion.li
                  key={skill}
                  variants={listItemVariants}
                  initial="hidden"
                  animate="visible"
                  custom={i}
                >
                  {skill}
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            className="col-md-6"
            variants={containerVariants}
            custom={0.5}
          >
            <h3 className="h6 fw-bold">Core Strengths</h3>
            <ul className="text-muted mt-2">
              {[
                "Clarity in communication",
                "Iterative problem-solving",
                "Teaching complex topics simply",
                "Designing maintainable systems",
              ].map((strength, i) => (
                <motion.li
                  key={strength}
                  variants={listItemVariants}
                  initial="hidden"
                  animate="visible"
                  custom={i}
                >
                  {strength}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </motion.section>

      {/* JOURNEY / TIMELINE */}
      <motion.section
        className="mb-5"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        custom={0.5}
      >
        <h2 className="h4 mb-3">My Journey</h2>

        <div className="position-relative ps-3 ps-md-4">
          <motion.div
            className="position-absolute top-0 bottom-0"
            style={{
              left: "8px",
              width: "2px",
              background:
                "linear-gradient(to bottom, rgba(59,130,246,0.4), rgba(15,23,42,0.1))",
            }}
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />

          {[
            "Started coding as a teenager — fascinated by how software shapes the world",
            "Built my first full-stack applications and discovered a passion for architecture",
            "Began teaching programming and mathematics",
            "Entered the digital health space and started designing solutions for seniors",
            "Today, I combine engineering, education, and innovation to build technology that helps people",
          ].map((item, index) => (
            <motion.div
              key={item}
              className="d-flex mb-3"
              variants={listItemVariants}
              initial="hidden"
              animate="visible"
              custom={index}
            >
              <div className="me-3 mt-1">
                <motion.div
                  className="rounded-circle"
                  style={{
                    width: 14,
                    height: 14,
                    border: "2px solid #3b82f6",
                    backgroundColor: "#fff",
                  }}
                  whileHover={{ scale: 1.2 }}
                />
              </div>
              <div className="text-muted">{item}</div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* CTA */}
      <motion.section
        className="text-center mt-5"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        custom={0.6}
      >
        <h2 className="h5 mb-3">Let’s Work Together</h2>

        <p className="text-muted mb-4" style={{ maxWidth: 700, margin: "0 auto" }}>
          If you’re looking for a technical partner, mentor, or someone who can turn
          complex ideas into real, scalable systems — I’d love to connect.
        </p>

        <motion.a
          href="mailto:your-email@example.com"
          className="btn btn-primary btn-lg"
          whileHover={{ y: -2, boxShadow: "0 12px 25px rgba(37,99,235,0.35)" }}
          whileTap={{ scale: 0.97 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
        >
          Contact Me
        </motion.a>
      </motion.section>
    </div>
  );
}
